import { useCallback, useEffect, useState } from 'react';

const DISMISS_KEY = 'smart_insight_notification_prompt_dismissed';

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
  return isIOS && isSafari;
}

function supportsNotificationPermission(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window && typeof Notification.requestPermission === 'function';
}

export type NotificationPermissionState = 'unsupported' | 'default' | 'granted' | 'denied';

export function useNotificationPermission() {
  const [permission, setPermission] = useState<NotificationPermissionState>(() => {
    if (!supportsNotificationPermission()) return 'unsupported';
    return Notification.permission as NotificationPermissionState;
  });
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');

  useEffect(() => {
    if (!supportsNotificationPermission()) {
      setPermission('unsupported');
      return;
    }
    setPermission(Notification.permission as NotificationPermissionState);
  }, []);

  const dismissPrompt = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  }, []);

  const requestPermission = useCallback(async () => {
    if (!supportsNotificationPermission()) return 'unsupported' as const;
    try {
      const result = await Notification.requestPermission();
      setPermission(result as NotificationPermissionState);
      if (result === 'granted' || result === 'denied') {
        dismissPrompt();
      }
      return result as NotificationPermissionState;
    } catch {
      return permission;
    }
  }, [dismissPrompt, permission]);

  const shouldShowPrompt =
    supportsNotificationPermission() &&
    !dismissed &&
    permission === 'default' &&
    !isIosSafari();

  return {
    permission,
    dismissed,
    shouldShowPrompt,
    requestPermission,
    dismissPrompt,
    isIosSafari: isIosSafari(),
  };
}
